/**
 * Per-action validity against the PROJECTED state of the CRM.
 *
 * This replaces action-to-action dependency edges for selection gating. The
 * old rule was "action B depends on action A, so deselecting A disables B".
 * That edge was derived from entity ownership, and when ownership was
 * mis-assigned — a no-op property update on a pre-existing deal claiming to
 * own it — every action that merely referenced the deal was disabled with
 * "depends on action #140194 which is not selected". The user saw actions
 * switch themselves off for a reason that named a number they had never seen.
 *
 * The rule here is about the world instead of about the actions:
 *
 *   An action is invalid when an entity it references will not exist —
 *   i.e. it is neither live in the CRM store, nor created by a currently
 *   SELECTED action in this proposal.
 *
 * That is the projection: live values ∪ what the selected actions produce. It
 * fixes the whole class rather than one instance, because deselecting a
 * property update can never invalidate anything — a property update does not
 * bring an entity into existence.
 *
 * It also agrees with the server by construction. The backend's
 * `find_dependencies` only draws an edge when an `ahoy_object_created` for
 * that entity is in the same batch — which is exactly the case where the
 * entity does not already exist. So "the backend will prune this" and "the
 * entity is missing from the projection" are the same condition.
 *
 * Fail-open: an entity we could not resolve (still loading, fetch failed) is
 * treated as PRESENT. A real action must never be disabled because the store
 * had not caught up — the server is the backstop, and a wrongly-disabled
 * action is a silently dropped change.
 */

import type { ProposedAction } from '../core/types';
import { getCreatedEntityId, getReferencedEntityIds } from './actionEntityRefs';

/**
 * Why an action cannot be applied. `missingEntityIds` are entity ids the UI
 * should render as badges — naming the thing that is missing is the whole
 * point ("Won't apply — Acme Corp is not being created").
 */
export interface ActionInvalidReason {
  kind: 'missing-entity';
  missingEntityIds: readonly string[];
}

export type ActionVerdict =
  | { status: 'valid' }
  | { status: 'invalid'; reason: ActionInvalidReason };

const VALID: ActionVerdict = { status: 'valid' };

export interface ProjectionInput {
  /** Every action in the proposal, including the deselected ones. */
  actions: ProposedAction[];
  /** Whether an action is currently selected. */
  isSelected: (actionId: number) => boolean;
  /**
   * Whether an entity exists in the CRM store right now. Return `undefined`
   * when the answer is not known yet (not fetched, fetch failed) — unknown
   * fails open and is treated as existing.
   */
  entityExists: (entityId: string) => boolean | undefined;
}

interface Creations {
  /** Entity ids a SELECTED action will bring into existence. */
  bySelected: Set<string>;
  /**
   * Entity ids created by ANY action in the proposal, selected or not.
   *
   * An id in here but not in `bySelected` is the central case: the proposal
   * creates this entity, but its create is deselected, so it will not exist.
   * That verdict must not depend on the store — a to-be-created entity has no
   * CRM record yet, so the store correctly reports "unknown", which fails
   * open. Checking `byAny` first is what makes the rule fire at all.
   */
  byAny: Set<string>;
}

function collectCreations(input: ProjectionInput): Creations {
  const bySelected = new Set<string>();
  const byAny = new Set<string>();
  for (const action of input.actions) {
    const entityId = getCreatedEntityId(action);
    if (entityId === null) {
      continue;
    }
    byAny.add(entityId);
    if (input.isSelected(action.id)) {
      bySelected.add(entityId);
    }
  }
  return { bySelected, byAny };
}

/**
 * Verdict for every action in the proposal, keyed by action id.
 *
 * Evaluated against one projection snapshot, so every action is judged
 * against the same world — no order dependence, and no need to walk actions
 * in `execution_order`.
 */
export function computeActionVerdicts(
  input: ProjectionInput
): Map<number, ActionVerdict> {
  const { bySelected, byAny } = collectCreations(input);
  const verdicts = new Map<number, ActionVerdict>();

  for (const action of input.actions) {
    const missing: string[] = [];

    for (const entityId of getReferencedEntityIds(action)) {
      if (bySelected.has(entityId)) {
        // A selected action creates it — it will exist by execution time.
        continue;
      }
      if (byAny.has(entityId)) {
        // The proposal creates it, but that create is deselected. Normally
        // such an entity has no CRM record yet, so the store cannot answer
        // this and the reference is missing. But a deselected create can share
        // its id with a record that already exists live — in which case that
        // live record still satisfies the projection. Only mark it missing
        // when the store does not confirm the record is present (`!== true`
        // covers both a confirmed-absent and a not-yet-resolved lookup, keeping
        // the rule firing for the ordinary to-be-created case).
        if (input.entityExists(entityId) !== true) {
          missing.push(entityId);
        }
        continue;
      }
      // Nothing in the proposal creates it, so it should already exist.
      // Unknown (undefined) fails open — never disable on a slow store.
      if (input.entityExists(entityId) === false) {
        missing.push(entityId);
      }
    }

    verdicts.set(
      action.id,
      missing.length === 0
        ? VALID
        : {
            status: 'invalid',
            reason: { kind: 'missing-entity', missingEntityIds: missing },
          }
    );
  }

  return verdicts;
}

/**
 * Every entity id the proposal references or creates — what the caller must
 * ensure is loaded into the store before `entityExists` can answer usefully.
 */
export function getProjectionEntityIds(
  actions: ProposedAction[]
): readonly string[] {
  const ids = new Set<string>();
  for (const action of actions) {
    for (const entityId of getReferencedEntityIds(action)) {
      ids.add(entityId);
    }
    const created = getCreatedEntityId(action);
    if (created !== null) {
      ids.add(created);
    }
  }
  return [...ids];
}
